import { create } from 'zustand';
import { devtools } from 'zustand/middleware';
import { postsApi, Post, PostFilters, PostStats, type BulkAddTagsResult } from '@/services/posts';
import { formatApiError } from '@/services/api';
import { devLog } from '@/utils/logger';

/**
 * Posts Store
 *
 * State management for the Creator dashboard:
 * - Post list with filters
 * - Post statistics
 * - Selection and bulk actions
 * - Pagination
 */

interface PostsState {
  // State
  posts: Post[];
  filters: PostFilters;
  selectedPosts: number[];
  stats: PostStats | null;
  isLoading: boolean;
  isLoadingStats: boolean;
  error: string | null;
  pagination: {
    page: number;
    limit: number;
    total: number;
    totalPages: number;
  };
  hasMore: boolean;

  // Circuit breaker state (prevent infinite retries)
  retryCount: number;
  lastRetryTime: number;
  circuitBreakerOpen: boolean;

  // Real-time publish trigger (updated by SSE when post is published)
  lastPublishTimestamp: number;

  // Actions - Data fetching
  fetchPosts: (append?: boolean) => Promise<void>;
  fetchStats: () => Promise<void>;
  /**
   * Cache-sync entry point (called by invalidateReleaseState). Refetches the list +
   * stats only while the Creator page is mounted; coalesces with an in-flight fetch
   * by queueing exactly one follow-up fetch (so a mutation that lands mid-request
   * is never masked by the older response).
   */
  refreshIfActive: () => Promise<void>;
  /**
   * Background refetch that keeps every currently loaded row ("Load more" pages)
   * without toggling isLoading. Refetches page 1 with limit = page * limit, then
   * restores the original per-page limit and page in pagination state.
   */
  silentRefreshPosts: () => Promise<void>;
  retryFetch: () => Promise<void>; // Manual retry

  // Actions - Filters
  setFilter: (key: keyof PostFilters, value: string | string[] | null) => void;
  clearFilters: () => void;
  /** Sync reset to a pristine state (team switch / logout). No fetch. */
  resetForTeamChange: () => void;

  // Actions - Selection
  selectPost: (id: number) => void;
  selectAllPosts: () => void;
  clearSelection: () => void;
  isPostSelected: (id: number) => boolean;

  // Actions - Post operations
  duplicatePost: (postId: number) => Promise<void>;
  deletePost: (postId: number) => Promise<void>;
  bulkDelete: () => Promise<void>;
  bulkDuplicate: () => Promise<void>;
  bulkReschedule: (scheduledFor: string) => Promise<void>;
  bulkAddLabels: (labels: string[]) => Promise<BulkAddTagsResult | null>;
  bulkAddHashtags: (hashtags: string[]) => Promise<BulkAddTagsResult | null>;
  exportPosts: () => Promise<void>;

  // Actions - Pagination
  setPage: (page: number) => void;
  setLimit: (limit: number) => void;
  loadMore: () => Promise<void>;

  // Actions - Error handling
  clearError: () => void;

  // Actions - Real-time updates
  setLastPublishTimestamp: (timestamp: number) => void;
}

const initialFilters: PostFilters = {
  status: 'ALL',
  platforms: null, // null means all platforms
  dateFrom: null,
  dateTo: null,
  search: '',
  contentStatus: null,
  publishingStatus: '',
  tags: '',
  channel: 'ALL',
  sortBy: 'newest',
  mediaType: null,
  aiGenerated: undefined,
  workflowStatus: null,
};

// Circuit breaker constants
const MAX_RETRIES = 3;
const RETRY_COOLDOWN = 10000; // 10 seconds
const CIRCUIT_BREAKER_THRESHOLD = 5; // Open circuit after 5 consecutive failures
const CIRCUIT_BREAKER_RESET = 60000; // Reset circuit after 1 minute

// Request deduplication (prevent multiple simultaneous requests)
let fetchPostsPromise: Promise<void> | null = null;
// Background refresh is tracked separately so fetchPosts() never dedupes onto it
// (it may have been built with stale filters). A normal fetch always wins.
let silentRefreshPromise: Promise<void> | null = null;
let normalFetchGeneration = 0;
let fetchStatsPromise: Promise<void> | null = null;

// Team-switch epoch. resetForTeamChange() bumps it; every async action captures it at
// start and drops its result (no state write) if it changed while the request was in
// flight. Otherwise a slow response from the previous team would repopulate the store.
let storeEpoch = 0;

// Cache-sync bridge: the Creator list lives in this store (not React Query), so
// invalidateReleaseState() can only reach it through refreshIfActive().
let listActive = false;
let postsRefetchQueued = false;
let statsRefetchQueued = false;

/** Creator page registers itself while mounted so cache-sync only refetches when visible. */
export function setPostsListActive(active: boolean): void {
  listActive = active;
}

export const usePostsStore = create<PostsState>()(
  devtools(
    (set, get) => ({
      // Initial state
      posts: [],
      filters: initialFilters,
      selectedPosts: [],
      stats: null,
      isLoading: false,
      isLoadingStats: false,
      error: null,
      pagination: {
        page: 1,
        limit: 20,
        total: 0,
        totalPages: 0,
      },
      hasMore: false,
      retryCount: 0,
      lastRetryTime: 0,
      circuitBreakerOpen: false,
      lastPublishTimestamp: 0,

      /**
       * Fetch posts with current filters
       * Includes: request deduplication, circuit breaker, retry limits
       * @param append - If true, append posts to existing array instead of replacing
       */
      fetchPosts: async (append = false) => {
        const state = get();

        // Request deduplication - if already fetching, return existing promise
        if (fetchPostsPromise) {
          // The caller may have just mutated data (or changed filters): the in-flight
          // response can predate that, so queue one more refresh after it settles.
          postsRefetchQueued = true;
          return fetchPostsPromise;
        }

        // Circuit breaker - check if circuit is open
        const now = Date.now();
        if (state.circuitBreakerOpen) {
          const timeSinceLastRetry = now - state.lastRetryTime;
          if (timeSinceLastRetry < CIRCUIT_BREAKER_RESET) {
            const remainingSeconds = Math.ceil((CIRCUIT_BREAKER_RESET - timeSinceLastRetry) / 1000);
            set({
              error: `Too many failed attempts. Please wait ${remainingSeconds} seconds before trying again.`,
              isLoading: false,
            });
            return;
          }
          // Circuit breaker timeout expired, reset
          devLog.log('[PostsStore] Circuit breaker reset after timeout');
          set({ circuitBreakerOpen: false, retryCount: 0 });
        }

        // Retry cooldown - prevent rapid retries
        const timeSinceLastRetry = now - state.lastRetryTime;
        if (state.retryCount >= MAX_RETRIES && timeSinceLastRetry < RETRY_COOLDOWN) {
          const remainingSeconds = Math.ceil((RETRY_COOLDOWN - timeSinceLastRetry) / 1000);
          set({
            error: `Too many failed attempts. Please wait ${remainingSeconds} seconds before trying again.`,
            isLoading: false,
          });
          return;
        }

        // Reset to page 1 for fresh loads (non-append)
        // This prevents showing only the last loaded page's posts when navigating back
        if (!append) {
          set((state) => ({
            pagination: {
              ...state.pagination,
              page: 1,
            },
          }));
        }

        set({ isLoading: true, error: null });
        normalFetchGeneration += 1; // invalidates any in-flight silent refresh
        const epoch = storeEpoch;

        fetchPostsPromise = (async () => {
          try {
            const { filters, pagination } = get();
            const data = await postsApi.list({
              ...filters,
              page: pagination.page,
              limit: pagination.limit,
            });

            if (epoch !== storeEpoch) return; // team switched mid-flight: drop result

            set((state) => ({
              posts: append ? [...state.posts, ...data.data] : data.data,
              pagination: {
                page: data.pagination.page,
                limit: data.pagination.limit,
                total: data.pagination.total,
                totalPages: data.pagination.pages,
              },
              hasMore: data.pagination.page < data.pagination.pages,
              isLoading: false,
              retryCount: 0, // Reset retry count on success
              lastRetryTime: 0,
              circuitBreakerOpen: false,
            }));

          } catch (error) {
            if (epoch !== storeEpoch) return; // team switched mid-flight: drop error too
            const state = get();
            const message = formatApiError(error).message;

            // Check if rate limited
            const isRateLimited = message.includes('429') || message.toLowerCase().includes('rate limit');

            // Increment retry count
            const newRetryCount = state.retryCount + 1;

            // Open circuit breaker if too many failures
            const shouldOpenCircuit = newRetryCount >= CIRCUIT_BREAKER_THRESHOLD;

            set({
              error: isRateLimited
                ? 'Rate limit exceeded. Please wait a few minutes before trying again.'
                : shouldOpenCircuit
                  ? `Service temporarily unavailable after ${newRetryCount} failed attempts. Please wait before trying again.`
                  : message,
              isLoading: false,
              retryCount: newRetryCount,
              lastRetryTime: Date.now(),
              circuitBreakerOpen: shouldOpenCircuit,
            });

            devLog.error('[PostsStore] Fetch posts failed:', {
              error: message,
              retryCount: newRetryCount,
              circuitBreakerOpen: shouldOpenCircuit,
            });

            // DO NOT auto-retry - let user manually retry
            // This prevents infinite loops
          } finally {
            // After a team switch the reset already cleared the dedupe slot (a new fetch
            // may own it now), so only touch shared state for the current epoch.
            if (epoch === storeEpoch) {
              fetchPostsPromise = null; // Reset promise for next request
              if (postsRefetchQueued) {
                postsRefetchQueued = false;
                if (listActive) void get().silentRefreshPosts();
              }
            }
          }
        })();

        return fetchPostsPromise;
      },

      /**
       * Fetch statistics
       * Includes: request deduplication (stats failures don't affect circuit breaker)
       */
      fetchStats: async () => {
        // Request deduplication - if already fetching, return existing promise
        if (fetchStatsPromise) {
          statsRefetchQueued = true; // in-flight response may predate the caller's mutation
          return fetchStatsPromise;
        }

        set({ isLoadingStats: true });
        const epoch = storeEpoch;

        fetchStatsPromise = (async () => {
          try {
            const stats = await postsApi.getStats();
            if (epoch !== storeEpoch) return; // team switched mid-flight: drop result
            set({ stats, isLoadingStats: false });
            devLog.log('[PostsStore] Fetch stats successful');
          } catch (error) {
            if (epoch !== storeEpoch) return;
            const message = formatApiError(error).message;
            // Stats errors don't affect the circuit breaker or retry count
            // They're non-critical and don't trigger infinite loops
            set({ isLoadingStats: false });
            devLog.error('[PostsStore] Fetch stats failed (non-critical):', message);
          } finally {
            if (epoch === storeEpoch) {
              fetchStatsPromise = null; // Reset promise for next request
              if (statsRefetchQueued) {
                statsRefetchQueued = false;
                if (listActive) void get().fetchStats();
              }
            }
          }
        })();

        return fetchStatsPromise;
      },

      refreshIfActive: async () => {
        if (!listActive) return;
        if (fetchPostsPromise || silentRefreshPromise) postsRefetchQueued = true;
        if (fetchStatsPromise) statsRefetchQueued = true;
        await Promise.all([
          fetchPostsPromise ? Promise.resolve() : get().silentRefreshPosts(),
          get().fetchStats(),
        ]);
      },

      silentRefreshPosts: async () => {
        if (fetchPostsPromise || silentRefreshPromise) {
          postsRefetchQueued = true;
          return;
        }
        // Don't hammer the API while the circuit breaker is open
        if (get().circuitBreakerOpen) return;

        const epoch = storeEpoch;
        const startGeneration = normalFetchGeneration;
        const startFilters = get().filters;
        const startPage = get().pagination.page;
        const startLimit = get().pagination.limit;
        const isStale = (): boolean =>
          epoch !== storeEpoch ||
          normalFetchGeneration !== startGeneration ||
          fetchPostsPromise !== null ||
          get().filters !== startFilters ||
          get().pagination.page !== startPage ||
          get().pagination.limit !== startLimit;

        silentRefreshPromise = (async () => {
          try {
            const { filters, pagination } = get();
            const loadedRows = Math.max(pagination.page, 1) * pagination.limit;
            // Backend caps limit at 100: refetch page-by-page when more rows are loaded
            const MAX_PAGE_SIZE = 100;
            let rows: Post[] = [];
            let total = pagination.total;
            let lastResponsePages = pagination.totalPages;
            if (loadedRows <= MAX_PAGE_SIZE) {
              const data = await postsApi.list({ ...filters, page: 1, limit: loadedRows });
              rows = data.data;
              total = data.pagination.total;
              lastResponsePages = Math.ceil(data.pagination.total / pagination.limit);
            } else {
              const seen = new Set<number>();
              for (let page = 1; page <= startPage; page++) {
                // Sequential, bounded by loaded pages; stop if superseded or breaker opened (429s)
                if (isStale() || get().circuitBreakerOpen) return;
                const data = await postsApi.list({ ...filters, page, limit: pagination.limit });
                for (const row of data.data) {
                  if (!seen.has(row.id)) {
                    seen.add(row.id);
                    rows.push(row);
                  }
                }
                total = data.pagination.total;
                lastResponsePages = data.pagination.pages;
                if (page >= data.pagination.pages) break;
              }
            }
            if (!listActive || isStale()) return;
            set((state) => {
              // Total may have shrunk (deletions): clamp loaded page to what exists
              const totalPages = Math.max(lastResponsePages, 0);
              const page = Math.min(state.pagination.page, Math.max(totalPages, 1));
              return {
                posts: rows,
                pagination: { ...state.pagination, page, total, totalPages },
                hasMore: page < totalPages,
              };
            });
          } catch (error) {
            if (epoch !== storeEpoch) return;
            // Silent: a failed background refresh keeps the current rows and does not
            // touch retry counters or the error banner.
            devLog.error('[PostsStore] Silent refresh failed (non-critical):', formatApiError(error).message);
          } finally {
            if (epoch === storeEpoch) {
              silentRefreshPromise = null;
              if (postsRefetchQueued && !fetchPostsPromise) {
                postsRefetchQueued = false;
                if (listActive) void get().silentRefreshPosts();
              }
            }
          }
        })();

        return silentRefreshPromise;
      },

      /**
       * Manual retry - resets circuit breaker and retry count
       */
      retryFetch: async () => {
        devLog.log('[PostsStore] Manual retry initiated - resetting circuit breaker');
        set({
          retryCount: 0,
          lastRetryTime: 0,
          circuitBreakerOpen: false,
          error: null,
        });
        await get().fetchPosts();
      },

      /**
       * Set filter value
       */
      setFilter: (key, value) => {
        set((state) => ({
          filters: {
            ...state.filters,
            [key]: value,
          },
          pagination: {
            ...state.pagination,
            page: 1, // Reset to first page when filtering
          },
          hasMore: false, // Reset hasMore when filtering
        }));

        // Auto-fetch posts when filter changes
        get().fetchPosts();
      },

      resetForTeamChange: () => {
        // Invalidate every in-flight request and free the dedupe slots so the new
        // team's first fetch does not piggyback on a stale promise.
        storeEpoch += 1;
        fetchPostsPromise = null;
        silentRefreshPromise = null;
        fetchStatsPromise = null;
        postsRefetchQueued = false;
        statsRefetchQueued = false;
        set({
          posts: [],
          filters: initialFilters,
          selectedPosts: [],
          stats: null,
          isLoading: false,
          isLoadingStats: false,
          error: null,
          pagination: { page: 1, limit: 20, total: 0, totalPages: 0 },
          hasMore: false,
          retryCount: 0,
          lastRetryTime: 0,
          circuitBreakerOpen: false,
        });
      },

      /**
       * Clear all filters
       */
      clearFilters: () => {
        set({
          filters: initialFilters,
          pagination: {
            page: 1,
            limit: 20,
            total: 0,
            totalPages: 0,
          },
          hasMore: false, // Reset hasMore when clearing filters
        });

        // Auto-fetch posts
        get().fetchPosts();
      },

      /**
       * Toggle post selection
       */
      selectPost: (id) => {
        set((state) => {
          const isSelected = state.selectedPosts.includes(id);
          return {
            selectedPosts: isSelected
              ? state.selectedPosts.filter((postId) => postId !== id)
              : [...state.selectedPosts, id],
          };
        });
      },

      /**
       * Select all visible posts
       */
      selectAllPosts: () => {
        set((state) => {
          const allPostIds = state.posts.map((post) => post.id);
          return { selectedPosts: allPostIds };
        });
      },

      /**
       * Clear selection
       */
      clearSelection: () => {
        set({ selectedPosts: [] });
      },

      /**
       * Check if post is selected
       */
      isPostSelected: (id) => {
        return get().selectedPosts.includes(id);
      },

      /**
       * Duplicate a post
       */
      duplicatePost: async (postId) => {
        set({ error: null });
        const epoch = storeEpoch;

        try {
          await postsApi.duplicate(postId);

          if (epoch !== storeEpoch) return;
          // Refresh posts and stats
          await Promise.all([get().fetchPosts(), get().fetchStats()]);
        } catch (error) {
          if (epoch !== storeEpoch) throw error;
          const message = formatApiError(error).message;
          set({ error: message });
          throw error;
        }
      },

      /**
       * Delete a single post
       */
      deletePost: async (postId) => {
        set({ error: null });
        const epoch = storeEpoch;

        try {
          await postsApi.delete(postId);

          if (epoch !== storeEpoch) return;
          // Refresh posts and stats
          await Promise.all([get().fetchPosts(), get().fetchStats()]);
        } catch (error) {
          if (epoch !== storeEpoch) throw error;
          const message = formatApiError(error).message;
          set({ error: message });
          throw error;
        }
      },

      /**
       * Delete selected posts
       */
      bulkDelete: async () => {
        const { selectedPosts } = get();

        if (selectedPosts.length === 0) {
          set({ error: 'No posts selected' });
          return;
        }

        set({ error: null, isLoading: true });
        const epoch = storeEpoch;

        try {
          await postsApi.bulkDelete(selectedPosts);

          // Clear selection and refresh
          if (epoch !== storeEpoch) return;
          set({ selectedPosts: [], isLoading: false });
          await Promise.all([get().fetchPosts(), get().fetchStats()]);
        } catch (error) {
          if (epoch !== storeEpoch) throw error;
          const message = formatApiError(error).message;
          set({ error: message, isLoading: false });
          throw error;
        }
      },

      /**
       * Duplicate selected posts
       */
      bulkDuplicate: async () => {
        const { selectedPosts } = get();

        if (selectedPosts.length === 0) {
          set({ error: 'No posts selected' });
          return;
        }

        set({ error: null, isLoading: true });
        const epoch = storeEpoch;

        try {
          // Duplicate each post individually (no bulk endpoint needed)
          await Promise.all(selectedPosts.map(id => postsApi.duplicate(id)));

          // Clear selection and refresh
          if (epoch !== storeEpoch) return;
          set({ selectedPosts: [], isLoading: false });
          await Promise.all([get().fetchPosts(), get().fetchStats()]);
        } catch (error) {
          if (epoch !== storeEpoch) throw error;
          const message = formatApiError(error).message;
          set({ error: message, isLoading: false });
          throw error;
        }
      },

      /**
       * Reschedule selected posts
       */
      bulkReschedule: async (scheduledFor) => {
        const { selectedPosts } = get();

        if (selectedPosts.length === 0) {
          set({ error: 'No posts selected' });
          return;
        }

        set({ error: null, isLoading: true });
        const epoch = storeEpoch;

        try {
          await postsApi.bulkReschedule(selectedPosts, scheduledFor);

          // Clear selection and refresh
          if (epoch !== storeEpoch) return;
          set({ selectedPosts: [], isLoading: false });
          await Promise.all([get().fetchPosts(), get().fetchStats()]);
        } catch (error) {
          if (epoch !== storeEpoch) throw error;
          const message = formatApiError(error).message;
          set({ error: message, isLoading: false });
          throw error;
        }
      },

      /**
       * Add labels to selected posts
       */
      bulkAddLabels: async (labels) => {
        const { selectedPosts } = get();

        if (selectedPosts.length === 0) {
          set({ error: 'No posts selected' });
          return null;
        }

        set({ error: null, isLoading: true });
        const epoch = storeEpoch;

        try {
          const result = await postsApi.bulkAddLabels(selectedPosts, labels);

          // Clear selection and refresh
          if (epoch !== storeEpoch) return null;
          set({ selectedPosts: [], isLoading: false });
          await Promise.all([get().fetchPosts(), get().fetchStats()]);
          return result;
        } catch (error) {
          if (epoch !== storeEpoch) throw error;
          const message = formatApiError(error).message;
          set({ error: message, isLoading: false });
          throw error;
        }
      },

      /**
       * Add hashtags to selected posts (appends to caption)
       */
      bulkAddHashtags: async (hashtags) => {
        const { selectedPosts } = get();

        if (selectedPosts.length === 0) {
          set({ error: 'No posts selected' });
          return null;
        }

        set({ error: null, isLoading: true });
        const epoch = storeEpoch;

        try {
          const result = await postsApi.bulkAddHashtags(selectedPosts, hashtags);

          // Clear selection and refresh
          if (epoch !== storeEpoch) return null;
          set({ selectedPosts: [], isLoading: false });
          await Promise.all([get().fetchPosts(), get().fetchStats()]);
          return result;
        } catch (error) {
          if (epoch !== storeEpoch) throw error;
          const message = formatApiError(error).message;
          set({ error: message, isLoading: false });
          throw error;
        }
      },

      /**
       * Export selected posts as CSV
       */
      exportPosts: async () => {
        const { selectedPosts } = get();

        if (selectedPosts.length === 0) {
          set({ error: 'No posts selected' });
          return;
        }

        set({ error: null, isLoading: true });
        const epoch = storeEpoch;

        try {
          await postsApi.exportPosts(selectedPosts);
          // Don't clear selection - user might want to do more actions
          if (epoch !== storeEpoch) return;
          set({ isLoading: false });
        } catch (error) {
          if (epoch !== storeEpoch) throw error;
          const message = formatApiError(error).message;
          set({ error: message, isLoading: false });
          throw error;
        }
      },

      /**
       * Set current page
       */
      setPage: (page) => {
        set((state) => ({
          pagination: {
            ...state.pagination,
            page,
          },
        }));

        // Auto-fetch posts when page changes
        get().fetchPosts();
      },

      /**
       * Set items per page
       */
      setLimit: (limit) => {
        set((state) => ({
          pagination: {
            ...state.pagination,
            limit,
            page: 1, // Reset to first page when limit changes
          },
        }));

        // Auto-fetch posts
        get().fetchPosts();
      },

      /**
       * Load more posts (append to existing posts)
       */
      loadMore: async () => {
        const state = get();

        // Don't load more if already loading or no more pages
        if (state.isLoading || !state.hasMore) {
          devLog.log('[PostsStore] Load more skipped:', {
            isLoading: state.isLoading,
            hasMore: state.hasMore,
          });
          return;
        }

        // Increment page and fetch with append mode (fetchPosts guards its own epoch)
        set((state) => ({
          pagination: {
            ...state.pagination,
            page: state.pagination.page + 1,
          },
        }));

        devLog.log('[PostsStore] Loading more posts, page:', state.pagination.page + 1);
        await get().fetchPosts(true); // Pass append=true
      },

      /**
       * Clear error message
       */
      clearError: () => {
        set({ error: null });
      },

      /**
       * Set last publish timestamp (called by SSE when post is published)
       * Calendar useEffect depends on this to refresh day drawer
       */
      setLastPublishTimestamp: (timestamp: number) => {
        set({ lastPublishTimestamp: timestamp });
      },
    }),
    {
      name: 'PostsStore',
      enabled: import.meta.env.DEV,
    }
  )
);
